import { utf8Encode } from './encoding';

// OWASP 2023 recommendation for PBKDF2-SHA256 (consumer-grade hardware).
// Raising this value strengthens the KDF at the cost of unlock latency.
export const PBKDF2_ITERATIONS = 600_000;
export const KDF_HASH = 'SHA-256';
export const AES_KEY_BITS = 256;

const HKDF_INFO_HMAC = utf8Encode('cruzfind.hmac.v1');
const HKDF_INFO_VERIFIER = utf8Encode('cruzfind.verifier.v1');
const HKDF_INFO_ANONID = utf8Encode('cruzfind.anonid.v1');

export interface DerivedKeys {
  /** AES-GCM key used to encrypt field plaintexts at rest. */
  aesKey: CryptoKey;
  /** HMAC-SHA256 key used to mint deterministic tokens from PII identifiers. */
  hmacKey: CryptoKey;
  /** HMAC-SHA256 key used solely to compute the verifier hash on unlock. */
  verifierKey: CryptoKey;
  /** HMAC-SHA256 key used to mint the anonymous session token sent to Box C. */
  anonIdKey: CryptoKey;
}

async function importPasswordKey(password: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    utf8Encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey'],
  );
}

async function deriveBaseBits(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<ArrayBuffer> {
  const pwKey = await importPasswordKey(password);
  return crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations, hash: KDF_HASH },
    pwKey,
    AES_KEY_BITS,
  );
}

async function importHkdfMaster(baseBits: ArrayBuffer): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', baseBits, 'HKDF', false, ['deriveKey']);
}

const HKDF_EMPTY_SALT = new Uint8Array(0);

async function hkdfAesKey(master: CryptoKey, info: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_EMPTY_SALT, info },
    master,
    { name: 'AES-GCM', length: AES_KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function hkdfHmacKey(master: CryptoKey, info: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_EMPTY_SALT, info },
    master,
    { name: 'HMAC', hash: 'SHA-256', length: 256 },
    false,
    ['sign', 'verify'],
  );
}

const HKDF_INFO_AES = utf8Encode('cruzfind.aes.v1');

export async function deriveKeysFromPassword(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<DerivedKeys> {
  const baseBits = await deriveBaseBits(password, salt, iterations);
  const master = await importHkdfMaster(baseBits);

  const [aesKey, hmacKey, verifierKey, anonIdKey] = await Promise.all([
    hkdfAesKey(master, HKDF_INFO_AES),
    hkdfHmacKey(master, HKDF_INFO_HMAC),
    hkdfHmacKey(master, HKDF_INFO_VERIFIER),
    hkdfHmacKey(master, HKDF_INFO_ANONID),
  ]);

  return { aesKey, hmacKey, verifierKey, anonIdKey };
}
